import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import {
  MAX_ZOOM,
  MIN_ZOOM,
  cropCentre,
  cropForZoom,
  framingStyle,
  hasLetterboxing,
  zoomOfCrop,
} from '../utils/printGeometry';
import type { CropSpec } from '../utils/editRecipe';
import styles from './PrintCropDialog.module.css';

interface PrintCropDialogProps {
  filename: string;
  imageUrl: string;
  /** Width / height of the source frame. */
  imageAspect: number;
  /** Width / height of the paper, already oriented to the image. */
  targetAspect: number;
  /** Human-readable print size, e.g. `4×6`. */
  sizeLabel: string;
  /** The line's current framing; `null` is the whole-frame letterbox. */
  crop: CropSpec | null;
  onCancel: () => void;
  onApply: (crop: CropSpec | null) => void;
}

/**
 * Frame a scan against a print size.
 *
 * The dialog offers exactly two states, because they are the two things a
 * customer actually wants: *fit the whole frame* (letterbox, nothing lost, white
 * on the sides) or *fill the paper* (drag and zoom, edges lost). There is no
 * free-form rectangle — the crop is locked to the paper's aspect throughout, so
 * whatever is inside the window is precisely what gets printed.
 *
 * Zoom is expressed relative to the fill framing: 1 is the largest slice of the
 * paper's aspect the image can supply, and higher values close in from there.
 * Anything below 1 would require showing beyond the edge of the image, which is
 * letterboxing — represented by having no crop at all rather than by an
 * out-of-bounds rectangle.
 */
export function PrintCropDialog({
  filename,
  imageUrl,
  imageAspect,
  targetAspect,
  sizeLabel,
  crop,
  onCancel,
  onApply,
}: PrintCropDialogProps) {
  const [fill, setFill] = useState(crop !== null);
  const [zoom, setZoom] = useState(() =>
    crop ? zoomOfCrop(crop, targetAspect, imageAspect) : MIN_ZOOM,
  );
  const [centre, setCentre] = useState(() => (crop ? cropCentre(crop) : { x: 0.5, y: 0.5 }));

  const windowRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; origin: { x: number; y: number } } | null>(
    null,
  );

  const previewCrop = fill
    ? cropForZoom(targetAspect, imageAspect, zoom, centre.x, centre.y)
    : null;

  // Escape cancels, matching the app's other dialogs.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Drag moves the *image* under a fixed window, so pointer movement maps to an
  // opposite shift of the crop centre. Distances are normalised by the crop's
  // own size so a drag tracks the image at any zoom level.
  const handlePointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (!fill) return;
      event.preventDefault();
      windowRef.current?.setPointerCapture?.(event.pointerId);
      dragRef.current = {
        startX: event.clientX,
        startY: event.clientY,
        origin: centre,
      };
    },
    [centre, fill],
  );

  const handlePointerMove = useCallback(
    (event: ReactPointerEvent) => {
      const drag = dragRef.current;
      const rect = windowRef.current?.getBoundingClientRect();
      if (!drag || !rect || rect.width === 0 || rect.height === 0 || !previewCrop) return;

      const dx = ((event.clientX - drag.startX) / rect.width) * previewCrop.width;
      const dy = ((event.clientY - drag.startY) / rect.height) * previewCrop.height;
      setCentre({ x: drag.origin.x - dx, y: drag.origin.y - dy });
    },
    [previewCrop],
  );

  // The crop the maths settled on may be clamped away from the requested centre
  // (a drag against the edge); reflect that back so the next drag starts from
  // the truth. Without this the raw centre keeps drifting past the edge and the
  // image rubber-bands — the customer has to drag all the way back before it
  // moves at all.
  const effectiveCentre = previewCrop ? cropCentre(previewCrop) : centre;

  const handlePointerUp = useCallback(
    (event: ReactPointerEvent) => {
      dragRef.current = null;
      windowRef.current?.releasePointerCapture?.(event.pointerId);
      setCentre(effectiveCentre);
    },
    [effectiveCentre],
  );

  const willLetterbox = !fill && hasLetterboxing(imageAspect, targetAspect);

  const imageStyle = framingStyle(previewCrop, imageAspect, targetAspect);

  return (
    <div className={styles.backdrop} role="presentation" onClick={onCancel}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={`Frame ${filename} for a ${sizeLabel} print`}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={styles.heading}>Frame for {sizeLabel}</h2>
        <p className={styles.filename}>{filename}</p>

        <div
          ref={windowRef}
          className={styles.window}
          style={{ aspectRatio: `${targetAspect}` }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          data-draggable={fill || undefined}
        >
          <img src={imageUrl} alt="" className={styles.image} style={imageStyle} draggable={false} />
        </div>

        <p className={styles.hint}>
          {fill
            ? 'Drag to reposition. Anything outside the window will not be printed.'
            : willLetterbox
              ? 'The whole frame is printed, with white filling the rest of the paper.'
              : 'This frame already matches the paper — nothing is cropped either way.'}
        </p>

        <fieldset className={styles.modes}>
          <legend className={styles.modesLegend}>How should it sit on the paper?</legend>
          <label className={styles.mode}>
            <input
              type="radio"
              name="print-crop-mode"
              checked={!fill}
              onChange={() => setFill(false)}
            />
            Fit the whole frame (nothing cut off)
          </label>
          <label className={styles.mode}>
            <input
              type="radio"
              name="print-crop-mode"
              checked={fill}
              onChange={() => setFill(true)}
            />
            Fill the paper (crop to fit)
          </label>
        </fieldset>

        <label className={styles.zoomRow} aria-disabled={!fill}>
          Zoom
          <input
            type="range"
            className={styles.zoom}
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.05}
            value={zoom}
            disabled={!fill}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
            aria-label="Zoom"
          />
          <span className={styles.zoomValue}>{zoom.toFixed(2)}×</span>
        </label>

        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.applyBtn}
            onClick={() =>
              onApply(
                fill
                  ? cropForZoom(targetAspect, imageAspect, zoom, effectiveCentre.x, effectiveCentre.y)
                  : null,
              )
            }
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
